import './assets/css/main.css'
import './assets/css/toast.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createRouter, createWebHistory } from 'vue-router'
import PrimeVue from 'primevue/config'
import Aura from "@primeuix/themes/aura"; // 导入 Aura 主题
import App from './App.vue'
import MainLayout from './layouts/MainLayout.vue'
import SimpleTopLayout from './layouts/SimpleTopLayout.vue'

// PrimeVue 样式
// import 'primevue/resources/themes/lara-light-blue/theme.css'
// import 'primevue/resources/primevue.min.css'
import 'primeicons/primeicons.css'

// PrimeVue 组件
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import Ripple from 'primevue/ripple'

// 创建应用实例
const app = createApp(App)

// 配置 Pinia
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(PrimeVue, {
    theme: {
      preset: Aura, // 使用 Aura 主题
      options: {
        darkModeSelector: false, // 禁用深色模式
        prefix: 'p',
        cssLayer: false
      }
    },
  });

// 配置 Vue Router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/login'
    },
    {
      path: '/login',
      component: () => import('./views/Login.vue'),
      beforeEnter: (to, from, next) => {
        const token = localStorage.getItem('token')
        if (token) {
          next('/devices')
        } else {
          next()
        }
      }
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: 'devices',
          component: () => import('./views/Devices.vue'),
          meta: { breadcrumb: 'Robot' }
        },
        {
          path: 'workspaces',
          component: () => import('./views/Workspaces.vue'),
          meta: { breadcrumb: 'Map Management' }
        },
        {
          path: 'workspaces/:id',
          component: () => import('./views/WorkspaceDetail.vue'),
          meta: { breadcrumb: 'Map Detail', parentBreadcrumb: 'Map Management', activeMenu: '/workspaces' }
        },
        {
          path: 'tasks',
          component: () => import('./views/Tasks.vue'),
          meta: { breadcrumb: 'Task List' }
        },
        {
          path: 'tasks/workspace/:workspaceId',
          component: () => import('./views/Tasks.vue'),
          meta: { breadcrumb: 'Task List', activeMenu: '/workspaces' }
        },
        {
          path: 'tasks/detail',
          component: () => import('./views/TasksDetail.vue'),
          meta: { breadcrumb: 'Task Detail', parentBreadcrumb: 'Task List' }
        },
        {
          path: 'charging-stations',
          component: () => import('./views/ChargingStations.vue'),
          meta: { breadcrumb: 'Charger' }
        },
        {
          path: 'charger/detail',
          component: () => import('./views/ChargerDetail.vue'),
          meta: { breadcrumb: 'Charger Detail', parentBreadcrumb: 'Charger' }
        },
        {
          path: 'messages',
          component: () => import('./views/Messages.vue'),
          meta: { breadcrumb: 'Message Management' }
        },
        {
          path: 'mine',
          component: () => import('./views/Mine.vue'),
          meta: { breadcrumb: 'User Center', parentBreadcrumb: 'Mine' }
        },
        {
          path: 'setting',
          component: () => import('./views/Setting.vue'),
          meta: { breadcrumb: 'Settings', parentBreadcrumb: 'Mine' }
        },
        {
          path: 'setting/my-group',
          component: () => import('./views/MyGroup.vue'),
          meta: { breadcrumb: 'My Group', parentBreadcrumb: 'Mine / Settings' }
        },
        {
          path: 'service-agreement',
          component: () => import('./views/ServiceAgreement.vue'),
          meta: { 
            breadcrumb: 'Service Agreement',
            parentBreadcrumb: 'Mine / Settings'
          }
        },
        {
          path: 'user-privacy-notice',
          component: () => import('./views/UserPrivacyNotice.vue'),
          meta: { 
            breadcrumb: 'User Privacy Notice',
            parentBreadcrumb: 'Mine / Settings'
          }
        },
        {
          path: 'devices/detail',
          component: () => import('./views/DeviceDetail.vue'),
          meta: { breadcrumb: 'Robot Detail', parentBreadcrumb: 'Robot', activeMenu: '/devices'  }
        },
        {
          path: 'devices/charging',
          component: () => import('./views/DeviceCharging.vue')
        },
        {
          path: 'device-errors',
          component: () => import('./views/DeviceErrors.vue'),
          meta: { breadcrumb: 'Equipment Error', parentBreadcrumb: 'Robot', activeMenu: '/devices' }
        },
        {
          path: 'device/upgrade',
          component: () => import('./views/DeviceUpgrade.vue'),
          meta: { breadcrumb: 'Robot Upgrade', parentBreadcrumb: 'Robot', activeMenu: '/devices' }
        },
      ]
    },
    {
      path: '/workspaces/:id/direction',
      component: SimpleTopLayout,
      children: [
        {
          path: '',
          component: () => import('./views/WorkspaceDirection.vue'),
          meta: { breadcrumb: 'Set Direction', parentBreadcrumb: 'Map Detail', activeMenu: '/workspaces' }
        }
      ]
    },
    {
      path: '/workspaces/edit/:id',
      component: SimpleTopLayout,
      children: [
        {
          path: '',
          component: () => import('./views/WorkspaceDetailInfo.vue'),
          meta: { breadcrumb: 'Edit Map', parentBreadcrumb: 'Map Detail', activeMenu: '/workspaces' }
        }
      ]
    },
    {
      path: '/workspaces',
      component: SimpleTopLayout,
      children: [
        {
          path: 'create',
          component: () => import('./views/WorkspaceDetailInfo.vue'),
          meta: { breadcrumb: 'Create Map', parentBreadcrumb: 'Map Management', activeMenu: '/workspaces' }
        },
        {
          path: 'select',
          component: () => import('./views/WorkspaceSelect.vue')
        }
      ]
    },
    {
      path: '/devices',
      component: SimpleTopLayout,
      children: [
        {
          path: 'select',
          component: () => import('./views/DeviceSelect.vue')
        },
        {
          path: 'mowing',
          component: () => import('./views/DeviceMowing.vue')
        },
        {
          path: 'control',
          component: () => import('./views/DeviceRemoteControl.vue')
        }
      ]
    },
    {
      path: '/map/base',
      component: SimpleTopLayout,
      children: [
        {
          path: '',
          component: () => import('./views/MapBase.vue')
        }
      ]
    },
  ]
})
app.use(router)

// 全局路由守卫：未登录跳转到登录页
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('token')
  if (to.path !== '/login' && !token) {
    next('/login')
  } else {
    next()
  }
})

// 配置 PrimeVue
app.use(ToastService)
app.use(ConfirmationService)
app.component('Toast', Toast)
app.directive('tooltip', Tooltip)
app.directive('ripple', Ripple)

// 挂载应用
window.$app = app
app.mount('#app')
